// Notes tool (Meeting Tools). A collaborative plain-text editor (CodeMirror 6)
// bound straight to the shared Y.Text in notesStore — live cursors, selections
// and presence come from Yjs awareness, so this component only draws the chrome
// (title, presence, mode, status, export). All sync/state lives in the store,
// outside the React tree, so typing never touches the video path.

import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap, placeholder } from '@codemirror/view';
import { ChangeEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { yCollab, yUndoManagerKeymap } from 'y-codemirror.next';

import { useCallStore } from '../../store/callStore';
import {
  exportNotes,
  type NotesMode,
  notesHandles,
  setNotesMode,
  setNotesTitle,
  useNotesStore,
} from '../../store/notesStore';
import { IconDownload } from '../../ui/icons';

const AUTHCOLORS_KEY = 'bz:notes-authcolors';

// The editor's look lives in our theme tokens (same document, so var() resolves).
const editorTheme = EditorView.theme(
  {
    '&': { color: 'var(--bz-color-text)', backgroundColor: 'transparent', fontSize: '14px', height: '100%' },
    '.cm-scroller': { fontFamily: 'inherit', overflow: 'auto', lineHeight: '1.7' },
    '.cm-content': { padding: '14px 16px 28px', caretColor: 'var(--bz-color-accent)' },
    '&.cm-focused': { outline: 'none' },
    '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--bz-color-accent)' },
    '.cm-selectionBackground, &.cm-focused .cm-selectionBackground': {
      backgroundColor: 'rgba(76,141,255,0.22)',
    },
    '.cm-placeholder': { color: 'var(--bz-color-text-muted)' },
  },
  { dark: true },
);

export const NotesTool = memo(function NotesTool() {
  const isHost = useCallStore((s) => s.isHost);
  const mode = useNotesStore((s) => s.mode);
  const title = useNotesStore((s) => s.title);
  const ready = useNotesStore((s) => s.ready);
  const bindKey = useNotesStore((s) => s.bindKey);
  const canEdit = mode === 'everyone' || isHost;

  const [showColors, setShowColors] = useState(() => {
    try {
      return localStorage.getItem(AUTHCOLORS_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const toggleColors = useCallback(() => {
    setShowColors((v) => {
      const next = !v;
      try {
        localStorage.setItem(AUTHCOLORS_KEY, next ? '1' : '0');
      } catch {
        /* private mode */
      }
      return next;
    });
  }, []);

  const onTitle = useCallback((e: ChangeEvent<HTMLInputElement>) => setNotesTitle(e.target.value), []);
  const onMode = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setNotesMode(e.currentTarget.dataset.mode as NotesMode);
  }, []);

  // Mount CodeMirror bound to the current Y.Text. Rebinds when edit rights
  // change (everyone⇄host-only, or host status) or the doc is recreated.
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = hostRef.current;
    if (!el || !ready) return;
    const { ytext, awareness } = notesHandles();
    // Awareness is passed for everyone so viewers still SEE the host's cursor; a
    // viewer's own caret is kept local by the store's broadcast gate. Editors
    // get yCollab's default undo history; read-only viewers get none.
    const collab = canEdit
      ? yCollab(ytext, awareness)
      : yCollab(ytext, awareness, { undoManager: false });
    const view = new EditorView({
      state: EditorState.create({
        doc: ytext.toString(),
        extensions: [
          history(),
          keymap.of([...defaultKeymap, ...historyKeymap, ...yUndoManagerKeymap]),
          EditorView.lineWrapping,
          placeholder(canEdit ? 'Jot the first note…' : 'The host is taking the notes.'),
          editorTheme,
          EditorView.editable.of(canEdit),
          EditorState.readOnly.of(!canEdit),
          collab,
        ],
      }),
      parent: el,
    });
    return () => view.destroy();
  }, [ready, canEdit, bindKey]);

  return (
    <div className="bz-notes">
      <div className="bz-notes-toolbar">
        <input
          className="bz-notes-title"
          value={title}
          onChange={onTitle}
          disabled={!canEdit}
          maxLength={120}
          aria-label="Notes title"
          spellCheck={false}
        />
        <NotesPresence />
      </div>

      <div className="bz-notes-modebar">
        {isHost ? (
          <div className="bz-notes-mode" role="group" aria-label="Who can edit">
            <button
              type="button"
              data-mode="everyone"
              className={mode === 'everyone' ? 'is-active' : ''}
              aria-pressed={mode === 'everyone'}
              onClick={onMode}
            >
              Everyone
            </button>
            <button
              type="button"
              data-mode="host-only"
              className={mode === 'host-only' ? 'is-active' : ''}
              aria-pressed={mode === 'host-only'}
              onClick={onMode}
            >
              Host only
            </button>
          </div>
        ) : (
          <span className="bz-notes-mode-label">
            {mode === 'host-only' ? 'The host is taking notes' : 'Everyone can edit'}
          </span>
        )}
        <button
          type="button"
          className="bz-notes-switch"
          role="switch"
          aria-checked={showColors}
          onClick={toggleColors}
        >
          <span className="bz-notes-switch-track" aria-hidden="true" />
          Author colors
        </button>
      </div>

      <div className={`bz-notes-editor${showColors ? '' : ' bz-notes-plain'}`} ref={hostRef} />

      <div className="bz-notes-status">
        <span className="bz-notes-synced">
          <span className="bz-notes-dot" aria-hidden="true" />
          Synced
        </span>
        <span className="bz-notes-sep">·</span>
        <span>Ephemeral</span>
        {mode === 'host-only' && (
          <>
            <span className="bz-notes-sep">·</span>
            <span className="bz-notes-hostflag">Host only</span>
          </>
        )}
      </div>

      <NotesExport />
    </div>
  );
});

// ── Presence avatars (from Yjs awareness; each dot's color IS that person's
// cursor color, so they always match) ───────────────────────────────────────
const NotesPresence = memo(function NotesPresence() {
  const peers = useNotesStore((s) => s.peers);
  const shown = peers.slice(0, 4);
  const extra = peers.length - shown.length;
  return (
    <div className="bz-notes-presence" aria-label="People in the notes">
      {shown.map((p) => {
        const title = p.isLocal ? `${p.name} (you)` : p.name;
        return (
          // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop -- leaf node, low-frequency
          <span key={p.key} className="bz-notes-ava" title={title} style={{ background: p.color, color: '#0a0f14' }}>
            {initials(p.name)}
          </span>
        );
      })}
      {extra > 0 && <span className="bz-notes-ava bz-notes-ava-more">+{extra}</span>}
    </div>
  );
});

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?';
}

// ── Export menu (ephemeral download) ─────────────────────────────────────────
const NotesExport = memo(function NotesExport() {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  const onPick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    exportNotes(e.currentTarget.dataset.fmt as 'md' | 'txt');
    setOpen(false);
  }, []);
  useEffect(() => {
    if (!open) return;
    const close = (): void => setOpen(false);
    // Close on the next click anywhere (the button's own click already toggled).
    const t = window.setTimeout(() => document.addEventListener('click', close, { once: true }), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('click', close);
    };
  }, [open]);

  return (
    <div className="bz-notes-foot">
      <span className="bz-notes-e2ee">Encrypted · never leaves your devices</span>
      <div className="bz-notes-export">
        <button type="button" className="bz-notes-save" onClick={toggle} aria-haspopup="menu" aria-expanded={open}>
          <IconDownload size={15} />
          Save
        </button>
        {open && (
          <div className="bz-notes-menu" role="menu">
            <button type="button" data-fmt="md" onClick={onPick}>
              Markdown (.md)
            </button>
            <button type="button" data-fmt="txt" onClick={onPick}>
              Plain text (.txt)
            </button>
          </div>
        )}
      </div>
    </div>
  );
});
